'use client';

import Script from 'next/script';
import { useCookieConsent } from '@/app/stores/cookie-consent-store';

const TIKTOK_PIXEL_ID = 'D9QC5RJC77U05N07MG9G';

const TikTokPixel = () => {
  const { isAccepted } = useCookieConsent();

  if (!isAccepted) return null;

  return (
    <Script id="tiktok-pixel" strategy="afterInteractive">
      {`
        !function (w, d, t) {
          w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
          var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
          ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};

          ttq.load('${TIKTOK_PIXEL_ID}');
          ttq.page();
        }(window, document, 'ttq');
      `}
    </Script>
  );
};

export default TikTokPixel;

// Only these events are forwarded to TikTok by `trackAllAnalyticsEvents`. TikTok is an ad platform
// rather than a general analytics tool, so we send it just the conversion-shaped events instead of
// the full firehose. Add a name here to start sending it.
export const TIKTOK_TRACKED_EVENTS = new Set(['first_ever_completion', 'signup_success']);

// TikTok can only optimize ad campaigns against events it defines, so ours are renamed on the way
// out to the closest standard event. An event absent from this map is sent under its own name.
const TIKTOK_EVENT_ALIASES: Record<string, string> = {
  first_ever_completion: 'StartTrial',
  signup_success: 'CompleteRegistration',
};

// TikTok matches on the SHA-256 of a lowercased, trimmed email; no other normalization (stripping
// dots, plus-addressing) is applied, or the hash stops matching the one TikTok computes its side.
const hashEmail = async (email: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(email.trim().toLowerCase()));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Sends one event to the TikTok pixel, optionally matching it to a person by email.
 *
 * The click that brought someone here needs no handling: the pixel snippet reads `ttclid` off the
 * landing URL and keeps it, with `_ttp`, in its own cookies, then attaches both to every event.
 * (`context.ad.callback` is the server-side Events API's name for that same click id -- there is no
 * pixel parameter for it.) Email is the one identifier the pixel can't find on its own, and it goes
 * through `identify` rather than the event params.
 */
export const trackTikTokEvent = (event: string, params?: Record<string, any>, email?: string) => {
  const ttq = typeof window !== 'undefined' ? (window as any).ttq : undefined;
  if (!ttq) return;

  const aliasedEvent = TIKTOK_EVENT_ALIASES[event] ?? event;
  const track = () => ttq.track(aliasedEvent, params);

  if (!email) {
    track();
    return;
  }

  // Hashing is async, so the event trails the identify call by a tick. Should hashing fail, still
  // send the event -- an unmatched conversion is worth more than a missing one.
  hashEmail(email)
    .then((hashedEmail) => ttq.identify({ email: hashedEmail }))
    .catch(() => {})
    .finally(track);
};
